<template>
	<a href="#" class="tab" :class="clazz" @click.prevent="click" :data-name="name" :draggable="true" @dragstart="dragStart" @dragend="dragEnd" ref="tabLink">
		<sym-icon v-if="hasIcon" :name="icon" v-bind="iconBind"></sym-icon>
		<span v-if="hasTitle" class="tab-label">{{ title }}</span>
		<span v-if="hasBadge" class="tab-badge">{{ badge }}</span>
	</a>
</template>

<script setup lang="ts">
import {ref, computed} from "vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";
import {IconTextMeasure, measureIconText} from "@/event/calendar/Shapes.ts";
import {ICON_FONT} from "@/event/calendar/Constants.ts";
import {objectRef, setDataTransfer} from "@/utils/DragDropUtils.ts";
import {SrcType} from "@t/meta/SourceType.ts";

const props = withDefaults(defineProps<{
	name: string;
	icon?: string,
	title?: string,
	badge?: string,

	active?: boolean,
	minify?: boolean
}>(), { active: false, minify: false });

const emit = defineEmits(["select"]);
const tabLink = ref<HTMLAnchorElement|null>(null);
const dragged = ref<boolean>(false);

const hasIcon = computed<boolean>(() => {
	return !!props.icon;
});

const hasTitle = computed<boolean>(() => {
	if (props.minify && hasIcon.value) return false;
	return !!props.title;
});

const iconBind = computed<any>(() => {
	if (props.minify && !!props.title) return {title: props.title };
	return null;
});

const hasBadge = computed<boolean>(() => {
	if (props.minify) return false;
	return !!props.badge;
});

const clazz = computed<string[]|null>(() => {
	const c:string[] = [];
	if (props.active) c.push("tab-active");
	if (dragged.value) c.push("tab-dragged");
	return c;
});

function click(e: MouseEvent):void {
	if (!props.active) {
		emit("select");
	}
}

function dragStart(e:DragEvent) {
	//e.preventDefault();
	const dragImage = createDragImage();
	e.dataTransfer.setDragImage(dragImage, e.offsetX + HPAD, e.offsetY + VPAD);
	//e.dataTransfer.setData('text/plain', props.title);
	setDataTransfer(e.dataTransfer, {}, objectRef(SrcType.SUBVIEW, props.name));
	e.dataTransfer.effectAllowed = "move";
	dragged.value = true;
}

function dragEnd(e:DragEvent) {
	dragged.value = false;
}

const HPAD = 8;
const VPAD = 4;
function createDragImage() {
	let height = 30;
	let bg = "#FFFFFF";
	let fg = "#003366";
	let font = "sans-serif";

	if (tabLink.value) {
		const m = getElementMeasure(tabLink.value);
		height = Math.round(m.height);

		const style = getComputedStyle(tabLink.value);
		bg = style.getPropertyValue("--form-bg");
		fg = style.getPropertyValue("--form-fg");
		font = style.getPropertyValue("--font");
	}
	const canvas:HTMLCanvasElement = document.createElement('canvas');
	canvas.style.width = "200px";
	canvas.style.height = height + "px";
	canvas.style.position = 'absolute';
	canvas.style.top = "-9000px";
	const ctx = canvas.getContext('2d');
	// Set canvas size
	canvas.width = window.devicePixelRatio *  200;
	canvas.height = window.devicePixelRatio *  height;
	ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

	// Draw tab here
	const m:IconTextMeasure = measureIconText(ctx, props.title, props.icon, font, 14);

	ctx.fillStyle = bg;
	ctx.beginPath();
	ctx.roundRect(0, 0, m.width + 2 * HPAD, height, 5);
	ctx.fill();
	ctx.fillStyle = fg;
	if (m.icon) {
		ctx.font = "14px "+ICON_FONT;
		ctx.fillText(m.icon, HPAD, m.ascent+VPAD);
	}
	if (m.text) {
		ctx.font = "14px "+font;
		ctx.fillText(m.text, HPAD + m.iconWidth, m.ascent+VPAD);
	}


	document.body.appendChild(canvas);
	setTimeout(() => canvas.remove(), 0);
	return canvas;
}
</script>

<style>
.tab-dragged {
	opacity: 0.25;
}
</style>